<script setup lang="ts">
import { useTeacherStore } from "@/store/teacher";
import { useSchoolStore } from "@/store/school";
import type {
	TCity,
	TTag,
	TTeacherData,
	TTeacherSearchFilters,
	TFieldOfStudy,
	TPagination,
} from "@/assets/js/globalTypes";
import type { TTeacherSearchParams } from "@/api/types";
import type { PageState } from "primevue/paginator";
import { formatPagination, getRequestPagination } from "@/assets/js/helpers";

const teacherStore = useTeacherStore();
const schoolStore = useSchoolStore();
const route = useRoute();
const isLoading = ref(true);
const teacherSearchData = ref<TTeacherData[]>([]);
const rows = ref(12);
const teacherSearchWrapper = ref<HTMLElement | null>(null);
const router = useRouter();

defineOptions({
	name: "school.teacher-search",
});
definePage({
	name: "school.teacher-search",
	path: "/school/teacher-search",
	redirect: "/school/teacher-search/general-search",
});

const getTeachers = async (
	params?: TTeacherSearchFilters,
	pagination?: PageState,
) => {
	isLoading.value = true;

	if (pagination) {
		const updatedPagination = formatPagination(pagination);
		schoolStore.setPagination(updatedPagination);
	}

	// Prepare the request parameters
	const processedParams: TTeacherSearchParams = params
		? preprocessFilterParams(params)
		: {};
	// Combine the processed parameters with pagination data
	const finalParams: TTeacherSearchParams & Partial<TPagination> = {
		...processedParams,
		...getRequestPagination(schoolStore.teacherSearchPagination, rows.value),
	};

	try {
		const response = await teacherStore.fetchAllTeachers(finalParams);

		teacherSearchData.value = response.data;
		schoolStore.teacherSearchPagination = response.meta?.pagination || null;
		teacherSearchWrapper.value?.scrollTo({ top: 0 });
	} catch (error) {
		console.error(error);
	} finally {
		isLoading.value = false;
	}
};

/**
 * Processes filter parameters by:
 * - Removing empty arrays
 * - Converting arrays into comma-separated strings
 * - Joining the 'id' values for 'skills', 'cities', and 'subjects' arrays
 */
const preprocessFilterParams = (
	params: Record<string, any>,
): TTeacherSearchParams => {
	return Object.fromEntries(
		Object.entries(params)
			.filter(([_, value]) => !(Array.isArray(value) && value.length === 0))
			.map(([key, value]) => {
				if (key === "skills" && Array.isArray(value)) {
					const tagIds = value.map((skill: TTag) => skill.tag_id).join(",");
					return [key, tagIds];
				} else if (key === "cities" && Array.isArray(value)) {
					const citIds = value.map((city: TCity) => city.cit_id).join(",");
					return [key, citIds];
				} else if (key === "subjects" && Array.isArray(value)) {
					const sfpIds = value
						.map((subject: TFieldOfStudy) => subject.sfp_id)
						.join(",");
					return [key, sfpIds];
				}
				return [key, Array.isArray(value) ? value.join(",") : value];
			}),
	);
};

async function applyFilters() {
	schoolStore.teacherSearchPagination = null;
	await getTeachers(schoolStore.teacherSearchSelectedFilters);
}

async function getTeachersByRoute() {
	if (route.name === "school.teacher-search.hired-teachers") {
		schoolStore.teacherSearchSelectedFilters = {
			...schoolStore.teacherSearchSelectedFilters,
			hired: true,
		};
		await getTeachers(schoolStore.teacherSearchSelectedFilters);
	} else {
		schoolStore.teacherSearchSelectedFilters = {
			...schoolStore.teacherSearchSelectedFilters,
			hired: undefined,
		};
		await getTeachers(schoolStore.teacherSearchSelectedFilters);
	}
}

watch(
	() => route.name,
	async () => {
		schoolStore.teacherSearchPagination = null;
		await getTeachersByRoute();
	},
);

onMounted(async () => {
	await getTeachersByRoute();
});

router.beforeEach((to, from) => {
	if (
		(to.name === "school.teacher-search.hired-teachers" ||
			to.name === "school.teacher-search.general-search") &&
		from.name !== "school.teacher-search.teacher-indivual-profile"
	) {
		schoolStore.teacherSearchPagination = null;
	}
});
</script>

<template>
	<div
		id="teacher-search"
		ref="teacherSearchWrapper"
	>
		<teacher-search-toolbar
			:hired-param="schoolStore.teacherSearchSelectedFilters.hired"
			@apply-filters="applyFilters()"
		/>
		<transition name="expand">
			<div v-if="!isLoading">
				<teacher-search-content
					:teacher-search-data="teacherSearchData"
					:is-loading="isLoading"
					:pagination="schoolStore.teacherSearchPagination"
					:rows="rows"
					@update-rows="(e) => (rows = e)"
					@change-page="
						(e) => getTeachers(schoolStore.teacherSearchSelectedFilters, e)
					"
				/>
			</div>
		</transition>

		<router-view />
	</div>
</template>

<style lang="scss" scoped>
$general-search-padding-top: 13px;

#teacher-search {
	padding: $general-search-padding-top 16px 49px;
	box-sizing: border-box;
	max-height: calc(100vh - $nav-top-bar - 1px);
	overflow-y: auto;
	overflow-x: hidden;
}
</style>
